import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Loader2, Plus, Search, Sparkles, Upload, X } from 'lucide-react'
import { toast } from 'sonner'
import { DashboardLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { createJob, getJobCompanies, type JobRow, type JobSort, type JobStage, type ParsedRequirements, listJobs, parseJd, parseJdFile } from '@/lib/api'
import { RoleRow, RolesHead } from '@/lib/rolesTable'
import { atsNav } from '@/lib/atsNav'
import { PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/jobs/')({ component: JobsPage })

const PAGE_SIZE = 25
const inputCls = 'h-9 rounded-md border border-input bg-card px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring'
const split = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean)

function JobsPage() {
  const [rows, setRows] = useState<JobRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [status, setStatus] = useState<'all' | 'pending' | 'screened'>('all')
  const [stage, setStage] = useState<JobStage | 'all'>('all')
  const [company, setCompany] = useState('')
  const [companies, setCompanies] = useState<{ value: string; count: number }[]>([])
  const [sort, setSort] = useState<JobSort>('recent')
  const [page, setPage] = useState(0)
  const [newOpen, setNewOpen] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    getJobCompanies().then((r) => setCompanies(r.companies)).catch(() => {})
  }, [reloadKey])
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(id)
  }, [search])
  useEffect(() => setPage(0), [debounced, status, stage, company, sort])

  useEffect(() => {
    let live = true
    setLoading(true)
    listJobs({ search: debounced || undefined, status, stage, company: company || undefined, sort, page, pageSize: PAGE_SIZE })
      .then((res) => {
        if (!live) return
        setRows(res.jobs)
        setTotal(res.total)
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [debounced, status, stage, company, sort, page, reloadKey])

  const from = total === 0 ? 0 : page * PAGE_SIZE + 1
  const to = Math.min((page + 1) * PAGE_SIZE, total)
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  return (
    <DashboardLayout title="Jobs" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <PageHeader title="Jobs" subtitle="Open roles — create a job, then screen it against the candidate pool." />
        <Button onClick={() => setNewOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> New job
        </Button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, owner, or ref…" className={`${inputCls} w-full pl-8`} />
        </div>
        <select value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls} aria-label="Filter by company">
          <option value="">All companies</option>
          {companies.map((c) => (
            <option key={c.value} value={c.value}>{c.value} ({c.count})</option>
          ))}
        </select>
        <select value={stage} onChange={(e) => setStage(e.target.value as JobStage | 'all')} className={inputCls} aria-label="Filter by stage">
          <option value="all">All stages</option>
          <option value="sourcing">Sourcing</option>
          <option value="screening">Screening</option>
          <option value="submitted">Submitted</option>
          <option value="interview">Interview</option>
          <option value="placement">Placement</option>
          <option value="archived">Archived</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className={inputCls} aria-label="Filter by status">
          <option value="all">All statuses</option>
          <option value="pending">Pending screening</option>
          <option value="screened">Screened</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as JobSort)} className={inputCls} aria-label="Sort">
          <option value="recent">Most recent</option>
          <option value="title">Title: A → Z</option>
        </select>
      </div>

      <div className="mb-2 text-xs text-muted-foreground">{loading ? 'Loading…' : `${total.toLocaleString()} job${total === 1 ? '' : 's'}`}</div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <RolesHead />
              </thead>
              <tbody className="divide-y divide-border">
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">No jobs match — create one with “New job”.</td>
                  </tr>
                ) : (
                  rows.map((j) => (
                    <RoleRow key={j.id} role={j} showCompany onClick={() => navigate({ to: '/jobs/$jobId', params: { jobId: j.id } })} />
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
            <span>{total === 0 ? 'No results' : `Showing ${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`}</span>
            <div className="flex items-center gap-1">
              <button disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))} className="inline-flex h-7 items-center gap-0.5 rounded-md border border-input bg-card px-2 disabled:opacity-40 enabled:hover:bg-muted/50">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <span className="px-1.5">Page {page + 1} of {(lastPage + 1).toLocaleString()}</span>
              <button disabled={page >= lastPage || loading} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} className="inline-flex h-7 items-center gap-0.5 rounded-md border border-input bg-card px-2 disabled:opacity-40 enabled:hover:bg-muted/50">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      <NewJobDrawer open={newOpen} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); setReloadKey((k) => k + 1) }} />
    </DashboardLayout>
  )
}

function NewJobDrawer({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [title, setTitle] = useState('')
  const [company, setCompany] = useState('')
  const [jd, setJd] = useState('')
  const [must, setMust] = useState('')
  const [nice, setNice] = useState('')
  const [formatted, setFormatted] = useState('')
  const [parsedReqs, setParsedReqs] = useState<ParsedRequirements>({}) // exp band / location / CTC / notice from the JD parse
  const [parsing, setParsing] = useState(false)
  const [saving, setSaving] = useState(false)
  const navigate = useNavigate()
  const fileRef = useRef<HTMLInputElement>(null)

  const applyParsed = (p: { company?: string; title: string; mustHave: string[]; niceToHave: string[]; formatted?: string; rawText?: string; requirements?: ParsedRequirements }) => {
    if (p.rawText) setJd(p.rawText) // keep the full JD body — esp. for file upload / URL fetch, where the textarea is empty
    if (p.requirements) setParsedReqs(p.requirements) // experience band / location / CTC / notice — so screening scores them
    if (p.company) setCompany(p.company)
    if (p.title) setTitle(p.title)
    if (p.mustHave.length) setMust(p.mustHave.join(', '))
    if (p.niceToHave.length) setNice(p.niceToHave.join(', '))
    if (p.formatted) setFormatted(p.formatted)
    if (!p.mustHave.length && !p.niceToHave.length) toast.error('No skills detected — fill them manually.')
    else toast.success('Requirements extracted from the JD')
  }
  const extract = useCallback(async () => {
    if (!jd.trim()) return
    setParsing(true)
    try {
      applyParsed(await parseJd(jd))
    } catch {
      toast.error('Could not parse the JD.')
    } finally {
      setParsing(false)
    }
  }, [jd])
  const onFile = useCallback(async (file: File) => {
    setParsing(true)
    try {
      applyParsed(await parseJdFile(file))
    } catch {
      toast.error('Could not read that file.')
    } finally {
      setParsing(false)
    }
  }, [])

  const create = useCallback(async (thenScreen: boolean) => {
    if (!title.trim() || !must.trim()) {
      toast.error('A title and at least one must-have skill are required.')
      return
    }
    setSaving(true)
    try {
      const { jobId } = await createJob({ title: title.trim(), company: company.trim() || undefined, mustHave: split(must), niceToHave: split(nice), jdRaw: jd.trim() || undefined, jdFormatted: formatted.trim() || undefined, ...parsedReqs })
      setTitle(''); setCompany(''); setJd(''); setMust(''); setNice(''); setFormatted(''); setParsedReqs({})
      if (thenScreen) navigate({ to: '/screen', search: { jobId } })
      else {
        toast.success('Job created')
        onCreated()
      }
    } catch {
      toast.error('Could not create the job — is the API on :8000?')
    } finally {
      setSaving(false)
    }
  }, [title, company, must, nice, jd, formatted, parsedReqs, onCreated, navigate])

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-40 bg-black/30" />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.22 }}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col overflow-y-auto border-l border-border bg-background shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <span className="text-sm font-medium">New job</span>
              <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3 p-5">
              <div className="rounded-lg border border-dashed border-border bg-muted/20 p-3">
                <div className="mb-2 text-xs font-medium text-muted-foreground">Paste or upload a JD — AI fills the requirements</div>
                <textarea value={jd} onChange={(e) => { setJd(e.target.value); setFormatted(''); setParsedReqs({}) }} rows={4} placeholder="Paste the job description…" className="w-full resize-y rounded-md border border-input bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={extract} disabled={parsing || !jd.trim()}>
                    {parsing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />} Extract requirements
                  </Button>
                  <span className="text-xs text-muted-foreground">or</span>
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={parsing}>
                    <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload JD file
                  </Button>
                  <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = '' }} />
                </div>
              </div>
              <label className="block text-xs text-muted-foreground">
                Title
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Senior Data Engineer" className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </label>
              <label className="block text-xs text-muted-foreground">
                Company (client)
                <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="e.g. Roche" className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </label>
              <label className="block text-xs text-muted-foreground">
                Must-have skills (comma-separated)
                <input value={must} onChange={(e) => setMust(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </label>
              <label className="block text-xs text-muted-foreground">
                Nice-to-have skills (comma-separated)
                <input value={nice} onChange={(e) => setNice(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
              </label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => create(false)} disabled={saving} className="flex-1">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Create job
                </Button>
                <Button onClick={() => create(true)} disabled={saving} className="flex-1">
                  <Sparkles className="mr-1.5 h-4 w-4" /> Create &amp; screen
                </Button>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
